import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '~/api';
import { useAutomations } from '~/features/settings/hooks/useAutomations';
import { useProtectionRates } from '~/features/settings/hooks/useProtectionRates';
import { useStoreId } from '~/providers/StoreUserProvider';
import { ConfigSettings } from '~/types';
import { isErrorMessage } from '~/utils/errorResponse';
import { useToast } from './useToast';

const useQueryKey = () => {
  const storeId = useStoreId();
  return ['configSettings', storeId] as const;
};

// TODO: the types get messed up when not using the select function
export const useConfigSettings = <Data>(
  select?: (data: ConfigSettings) => Data,
) => {
  const queryKey = useQueryKey();

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, storeId] }) =>
      api.store(storeId).settings.config.get(),
    select,
  });
};

export const useCheckoutPlusEmbed = () => {
  const storeId = useStoreId();

  return useQuery({
    queryKey: ['checkoutPlusEmbed', storeId],
    queryFn: () => api.store(storeId).shippingProtection.checkoutPlusEmbed(),
  });
};

export const useConfigureGsp = () => {
  const storeId = useStoreId();
  const queryClient = useQueryClient();
  const queryKey = useQueryKey();
  const { refetch: refetchAutomations } = useAutomations();
  const { refetch: refetchRates } = useProtectionRates();
  const { refetch: refetchConfigSettings } = useConfigSettings();

  return useMutation({
    mutationFn: api.store(storeId).shippingProtection.configure,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({
        queryKey: ['checkoutPlusEmbed', storeId],
      });
      await refetchAutomations();
      await refetchRates();
      await refetchConfigSettings();
    },
  });
};

export const useIsShippingTosCheckboxEnabled = () => {
  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  return !!data?.widgetConfig.shippingPlus.isFreeShippingOptInEnabled;
};

export const useConfigSettingsUpdate = () => {
  const queryKey = useQueryKey();
  const queryClient = useQueryClient();
  const storeId = useStoreId();
  const toast = useToast();

  return useMutation({
    mutationFn: (values: Partial<ConfigSettings>) => {
      const data = queryClient.getQueryData<ConfigSettings>(queryKey);

      if (!data) {
        return Promise.reject(new Error('No existing Config Settings Data'));
      }

      return api.store(storeId).settings.config.put({ ...data, ...values });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error) => {
      const message = isErrorMessage(error)
        ? error.message
        : 'Something went wrong. Please try again.';

      toast.show(message);
    },
  });
};
